import { useEffect, useMemo, useState } from 'react'
import type { User } from '../types'
import {
  getItAssetsViewers,
  loadUsers,
  setItAssetsViewers,
} from '../lib/apiPhpAdapter'
import { canAccessItAdminRestrictedTable } from '../lib/roleFinancialAccess'
import { filterUsersForPicker } from '../lib/userListFilters'
import { sortByPersonName } from '../utils/personNameSort'
import './ItAssetsAccessPanel.css'

/**
 * Nastavení: kdo vidí tlačítko „Otevřít evidenci IT majetku“.
 * Správce IT má přístup vždy (nemusí být v seznamu).
 */
export function ItAssetsAccessPanel() {
  const [users, setUsers] = useState<User[]>([])
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [configured, setConfigured] = useState(false)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const [allUsers, viewers] = await Promise.all([loadUsers(), getItAssetsViewers()])
        if (cancelled) return
        const list = sortByPersonName(filterUsersForPicker(Array.isArray(allUsers) ? allUsers : []))
        setUsers(list)
        setConfigured(!!viewers.configured)
        if (viewers.configured) {
          setSelected(new Set((viewers.user_ids || []).map(String)))
        } else {
          // Předvybrat stávající legacy role, aby se při prvním uložení nic „neztratilo“
          const legacy = list.filter((u) => canAccessItAdminRestrictedTable(u) && u.id)
          setSelected(new Set(legacy.map((u) => String(u.id))))
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Nepodařilo se načíst')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        String(u.role || '').toLowerCase().includes(q),
    )
  }, [users, search])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    setOkMsg(null)
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    setOkMsg(null)
    try {
      const res = await setItAssetsViewers(Array.from(selected))
      setConfigured(!!res.configured)
      setSelected(new Set((res.user_ids || []).map(String)))
      setOkMsg('Seznam uživatelů s přístupem k evidenci IT majetku byl uložen.')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Uložení selhalo')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="it-assets-access loading">Načítání…</div>
  }

  return (
    <div className="it-assets-access settings-section">
      <h2>Evidence IT majetku — kdo vidí tlačítko</h2>
      <p className="it-assets-access__intro">
        Vyberte uživatele, kterým se na stránce <strong>Informace od IT administrátora</strong>{' '}
        zobrazí tlačítko <strong>Otevřít evidenci IT majetku</strong>. Správce IT má přístup vždy.
        {!configured ? (
          <>
            {' '}
            Seznam ještě nebyl uložen — předvyplněni jsou uživatelé podle dřívějších rolí (uložením
            přepnete na vlastní seznam).
          </>
        ) : null}
      </p>

      {error ? <div className="it-assets-access__error">{error}</div> : null}
      {okMsg ? <div className="it-assets-access__ok">{okMsg}</div> : null}

      <input
        type="search"
        className="it-assets-access__search"
        placeholder="Hledat jméno, e-mail nebo roli…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <p className="it-assets-access__count">
        Vybráno: {selected.size} · zobrazeno: {filtered.length} / {users.length}
      </p>

      <div className="it-assets-access__list">
        {filtered.map((u) => {
          const id = String(u.id)
          const checked = selected.has(id)
          const isIt =
            u.role === 'admin' && (u as User & { admin_type?: string }).admin_type === 'spravce_it'
          return (
            <label key={id} className="it-assets-access__row">
              <input
                type="checkbox"
                checked={checked || isIt}
                disabled={isIt}
                onChange={() => toggle(id)}
              />
              <span>
                <strong>{u.name}</strong>
                {isIt ? ' · správce IT (vždy)' : ''}
                <br />
                <span className="it-assets-access__meta">
                  {u.email || '—'}
                  {u.role ? ` · ${u.role}` : ''}
                </span>
              </span>
            </label>
          )
        })}
        {filtered.length === 0 ? (
          <p className="it-assets-access__meta">Žádný uživatel neodpovídá hledání.</p>
        ) : null}
      </div>

      <button type="button" className="it-assets-access__save" disabled={saving} onClick={() => void save()}>
        {saving ? 'Ukládám…' : 'Uložit přístup'}
      </button>
    </div>
  )
}
